import { http } from '@/app/lib/http/client'
import { ListParams, PaginatedResponse } from '../types/pagination'
import type { InventoryAllocation, InventorySourcesSnapshot } from './pos.service'

// ─── Interfaces del dominio ──────────────────────────────────────────────────

// ---------------------------------------------------------------------------
// Creacion de Producto
// ---------------------------------------------------------------------------

/** Un archivo de imagen con su preview local */
export interface ImageFile {
  file: File
  previewUrl: string
  /** Para galería: orden de visualización (0 = primero) */
  sortOrder?: number
}

/** Formulario de creación de producto. Los campos con sufijo *Id son IDs de catálogo. */
export interface NewProduct {
  // --- Identificación ---
  name: string
  description: string
  barcode: string

  // --- Imágenes ---
  /** Imagen principal — se subirá y luego se enviará como hero_image (URL) */
  heroImage: ImageFile | null
  /** Galería adicional — se sube y se envía como images[] */
  galleryImages: ImageFile[]

  // --- Catálogo (IDs) ---
  categoryId: string
  departmentId: string
  productTypeId: string
  unitId: string
  satProductKeyId: string
  satProductKeyCode?: string
  satProductKeyName?: string

  // --- Precio (objeto price del backend) ---
  costWithoutTaxes: string
  costWithTaxes: string
  profitPercentage: string

  // --- Impuesto (tax_ids — array tamaño 1) ---
  taxId: string

  // --- Lote/batch (solo para tipos que requires_lot = true) ---
  batchNumber: string
  expirationDate: string
  requiresLot?: boolean

  // --- Rotación por sucursal (Frontend only por ahora) ---
  rotationByBranch: Array<{
    sucursal_id: string
    stock_minimo: string
    stock_maximo: string
  }>

  // --- Precios por cantidad (price_tiers[]) ---
  pricingTiers: PricingTier[]
}

/** Tier de precio por cantidad — mapea a price_tiers[] del backend */
export interface PricingTier {
  tierId?: number
  /** min_quantity en el backend */
  minQuantity: number
  /** profit_percentage en el backend */
  profitPercentage: number
  /** discount_percentage en el backend */
  discountPercentage: number
  startsAt: string
  endsAt: string
  isActive: boolean
  lifecycleStatus?: 'ACTIVE' | 'SCHEDULED' | 'EXPIRED' | 'INACTIVE'
  /** Calculado localmente para mostrar vista previa; no se envía al backend */
  price: string
  /** Calculado localmente para mostrar vista previa; no se envía al backend */
  priceWithTax: string
}
export interface ProductListItem {
  id: number
  name: string
  barcode: string
  description?: string
  is_active: boolean
  hero_image?: string
  hero_image_url?: string
  category?: { id: number; name: string }
  department?: { id: number; name: string }
  product_type?: {
    id: number
    name: string
    requires_lot: number
    requires_prescription: number
    regulatory_class?: 'ANTIBIOTIC' | 'CONTROLLED' | null
  }
  unit?: { id: number; name: string }
  price?: {
    cost_without_taxes: number
    cost_with_taxes: number
    profit_percentage: number
    sale_price?: number
    final_price?: number
  }
  stocks?: Array<{
    sucursal_id: number
    stock: number
    stock_min: number
    stock_max: number
    sucursal?: { id: number; name: string }
    inventory?: {
      physical: number
      batch_physical: number
      free_physical: number
      reserved: number
      sellable: number
      sources: Array<{
        source_type: 'unbatched' | 'batch'
        batch_id: number | null
        batch_number: string | null
        expiration_date: string | null
        is_expired: boolean
        physical: number
        reserved: number
        available: number
      }>
    }
  }>
  created_at?: string
  updated_at?: string
}

export interface ProductDetail extends ProductListItem {
  sat_product_key?: { id: number; description: string; code: string }
  taxes?: Array<{ id: number; name: string; percentage: number }>
  images?: Array<{ id: number; image_url: string; sort_order: number; is_primary: boolean }>
  batches?: Array<{
    id: number
    batch_number: string
    expiration_date: string
    quantity: number
  }>
  price_tiers?: Array<{
    id: number
    min_quantity: number
    profit_percentage: number
    discount_percentage: number
    starts_at: string
    ends_at: string
    is_active: boolean
    is_effective: boolean
    lifecycle_status: 'ACTIVE' | 'SCHEDULED' | 'EXPIRED' | 'INACTIVE'
  }>
  stock_by_branch?: Array<{
    sucursal_id: number
    sucursal_name: string
    stock: number
    stock_minimo: number
    stock_maximo: number
  }>
}

export interface ProductListFilters extends ListParams {
  search?: string
  category_id?: number
  department_id?: number
  product_type_id?: number
  is_active?: boolean
  /**
   * Cuando es 'only', retorna SOLO los productos con soft-delete.
   * El backend usa Product::onlyTrashed() cuando recibe trashed=only.
   */
  trashed?: 'true'
}

export interface UpdateProductPayload {
  name?: string
  description?: string
  barcode?: string
  is_active?: boolean
  category_id?: number
  department_id?: number
  product_type_id?: number
  unit_id?: number
  sat_product_key_id?: number
  sat_product_key_code?: string
  sat_product_key_name?: string
  price?: {
    cost_without_taxes?: number
    cost_with_taxes?: number
    profit_percentage?: number
  }
  tax_ids?: number[]
  price_tiers?: Array<{
    id?: number
    min_quantity: number
    profit_percentage: number
    discount_percentage: number
    starts_at: string
    ends_at: string
    is_active: boolean
  }>
  deleted_price_tier_ids?: number[]
  /** Archivo de imagen principal (se añade al FormData como hero_image_file) */
  heroImageFile?: File
  /** Flag para eliminar la imagen principal */
  remove_hero_image?: boolean
  /** Archivos de galería nuevos (se añaden como images[i][file]) */
  galleryFiles?: File[]
  delete_image_ids?: number[]
}

export interface StoreProductPayload {
  name: string
  barcode: string
  description?: string
  is_active?: boolean
  category_id: number
  department_id: number
  product_type_id: number
  unit_id: number
  sat_product_key_id?: number
  sat_product_key_code?: string
  sat_product_key_name?: string
  price: {
    cost_without_taxes?: number
    cost_with_taxes?: number
    profit_percentage: number
  }
  tax_ids: number[]
  price_tiers?: Array<{
    min_quantity: number
    profit_percentage: number
    discount_percentage: number
    starts_at: string
    ends_at: string
    is_active: boolean
  }>
  batch?: {
    batch_number?: string
    expiration_date?: string
  }
  /** Archivo de imagen principal */
  heroImageFile?: File
  /** Archivos de galería */
  galleryFiles?: File[]
}

export interface StockAdjustmentPayload {
  type: 'add' | 'subtract'
  quantity: number
  reason: string
  sucursal_id?: number
  batch_mode?: 'new' | 'existing'
  product_batch_id?: number
  batch_number?: string
  expiration_date?: string
  allocations?: InventoryAllocation[]
}

export interface UpdateProductStockThresholdsPayload {
  sucursal_id: number
  stock_min: number
  stock_max: number
}

// ─── Helper: construye FormData desde un payload con archivos ────────────────

/**
 * Serializa un objeto plano como FormData.
 * - Las claves con valor `File` se agregan como archivo binario.
 * - Los arrays y objetos anidados se expanden con notación Laravel (key[i][sub]).
 * - Los valores `null` / `undefined` se omiten.
 */
function buildFormData(payload: Record<string, unknown>): FormData {
  const fd = new FormData()

  function append(key: string, value: unknown) {
    if (value === null || value === undefined) return

    if (value instanceof File) {
      fd.append(key, value)
    } else if (Array.isArray(value)) {
      value.forEach((item, i) => {
        if (item !== null && item !== undefined) {
          if (typeof item === 'object' && !(item instanceof File)) {
            Object.entries(item as Record<string, unknown>).forEach(([subKey, subVal]) => {
              append(`${key}[${i}][${subKey}]`, subVal)
            })
          } else {
            append(`${key}[${i}]`, item)
          }
        }
      })
    } else if (typeof value === 'object') {
      Object.entries(value as Record<string, unknown>).forEach(([subKey, subVal]) => {
        append(`${key}[${subKey}]`, subVal)
      })
    } else {
      fd.append(key, String(value))
    }
  }

  Object.entries(payload).forEach(([key, value]) => append(key, value))
  return fd
}

// ─── Servicio ────────────────────────────────────────────────────────────────

export const productService = {
  /**
   * Obtiene la lista paginada de productos.
   * - `trashed: 'only'` → devuelve solo los eliminados con soft-delete.
   */
  async getProducts(params?: ProductListFilters): Promise<PaginatedResponse<ProductListItem>> {
    const response = await http.get('/products', { params })
    const d = response.data
    if (d?.data && Array.isArray(d.data)) return d
    if (d?.data?.data && Array.isArray(d.data.data)) return d.data
    return d
  },

  /** Obtiene el detalle completo de un producto */
  async getProduct(id: number): Promise<ProductDetail> {
    const response = await http.get(`/products/${id}`)
    const d = response.data
    return d?.data ?? d
  },

  /** Revisa la disponibilidad de un código de barras */
  async checkBarcode(barcode: string, ignoreProductId?: number) {
    const response = await http.get('/products/barcode/check', {
      params: { barcode, ...(ignoreProductId ? { ignore_product_id: ignoreProductId } : {}) }
    })
    const d = response.data
    return d?.data ?? d
  },

  /**
   * Crea un nuevo producto enviando un FormData multipart.
   * Las imágenes se incluyen directamente en la petición de creación
   * como hero_image_file y images[i][file], sin endpoint separado.
   */
  async createProduct(payload: StoreProductPayload) {
    const { heroImageFile, galleryFiles, ...rest } = payload

    // Construimos el objeto base sin archivos para serializar
    const base: Record<string, unknown> = { ...rest }

    // Galería como array de objetos con sort_order e is_primary
    if (galleryFiles && galleryFiles.length > 0) {
      base.images = galleryFiles.map((file, i) => ({
        file,
        sort_order: i,
        is_primary: false,
      }))
    }

    const fd = buildFormData(base)

    // Imagen hero va aparte con la clave que espera el backend
    if (heroImageFile) {
      fd.append('hero_image_file', heroImageFile)
    }

    const response = await http.post('/products', fd, {
      headers: { 'Content-Type': 'multipart/form-data' },
    })
    return response.data
  },

  /**
   * Actualiza un producto existente.
   * Si hay archivos de imagen, usa FormData multipart.
   * Si no hay archivos, envía JSON normal.
   */
  async updateProduct(id: number, payload: UpdateProductPayload) {
    const { heroImageFile, galleryFiles, ...rest } = payload

    const hasNewFiles = Boolean(heroImageFile) || (galleryFiles && galleryFiles.length > 0)

    // Multipart cuando hay archivos nuevos; las eliminaciones de galería van bien en JSON.
    if (!hasNewFiles) {
      const response = await http.put(`/products/${id}`, rest)
      return response.data
    }

    // Con archivos → multipart FormData
    // Laravel con PUT y FormData necesita el método spoofing
    const base: Record<string, unknown> = { ...rest, _method: 'PUT' }

    if (galleryFiles && galleryFiles.length > 0) {
      base.images = galleryFiles.map((file, i) => ({
        file,
        sort_order: i,
        is_primary: false,
      }))
    }

    const fd = buildFormData(base)

    if (heroImageFile) {
      fd.append('hero_image_file', heroImageFile)
    }

    // Se envía como POST con _method=PUT para que Laravel procese los archivos
    const response = await http.post(`/products/${id}`, fd, {
      headers: { 'Content-Type': 'multipart/form-data' },
    })
    return response.data
  },

  /** Ajuste de stock (entrada / salida manual) */
  async adjustStock(id: number, payload: StockAdjustmentPayload) {
    const response = await http.post(`/products/${id}/adjust-stock`, payload)
    return response.data
  },

  async getInventorySources(id: number, sucursalId: number, quantity: number): Promise<InventorySourcesSnapshot> {
    const response = await http.get(`/products/${id}/inventory-sources`, {
      params: { sucursal_id: sucursalId, quantity },
    })
    return response.data.data
  },

  /** Cambia el estado is_active del producto */
  async toggleStatus(id: number, isActive: boolean) {
    const response = await http.patch(`/products/${id}/status`, { is_active: isActive })
    return response.data
  },

  /** Soft-delete: elimina lógicamente el producto */
  async deleteProduct(id: number) {
    const response = await http.delete(`/products/${id}`)
    return response.data
  },

  /** Restaura un producto eliminado con soft-delete */
  async restoreProduct(id: number) {
    const response = await http.post(`/products/${id}/restore`)
    return response.data
  },

  /** Actualiza los parámetros de stock mínimo y máximo por sucursal */
  async updateStockThresholds(id: number, payload: UpdateProductStockThresholdsPayload) {
    const response = await http.patch(`/products/${id}/stock-thresholds`, payload)
    return response.data
  },
}
